import { toast } from 'sonner';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function LandingPage() {
  const [formData, setFormData] = useState({
    nombre: '', email: '', telefono: '', capital_disponible: '',
    tipo_inversion: 'short_term', invitation_code: '', notas: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [codeValid, setCodeValid] = useState(null);
  const [minAmount, setMinAmount] = useState(40000);
  const [rateTiers, setRateTiers] = useState([]);
  const [landingSettings, setLandingSettings] = useState({});
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isCa = i18n.language === 'ca';

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const [tiersRes, settingsRes] = await Promise.all([
          axios.get(`${API_URL}/api/rate-tiers`),
          axios.get(`${API_URL}/api/public/landing-settings`)
        ]);
        if (tiersRes.data.tiers?.length) {
          setRateTiers(tiersRes.data.tiers);
          setMinAmount(Math.min(...tiersRes.data.tiers.map(t => t.min_capital)));
        }
        setLandingSettings(settingsRes.data);
      } catch (e) { console.error(e); }
    };
    fetchData();
    if (!localStorage.getItem('language')) i18n.changeLanguage('ca');
  }, []);

  const validateInvitationCode = async (code) => {
    if (!code || code.length < 3) { setCodeValid(null); return; }
    try {
      const response = await axios.get(`${API_URL}/api/public/validate-invitation/${code}`);
      setCodeValid(response.data.valid);
    } catch (error) { setCodeValid(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!codeValid) { toast.info(t('waitlist.codeRequired')); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/public/waitlist`, {
        ...formData, capital_disponible: parseFloat(formData.capital_disponible)
      });
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar solicitud');
    } finally { setSubmitting(false); }
  };

  const inputStyle = {
    width: '100%', padding: '0.875rem', background: '#f8fafc',
    border: '1.5px solid #e2e8f0', borderRadius: '10px',
    color: '#1e293b', fontSize: '0.95rem', outline: 'none',
    boxSizing: 'border-box', transition: 'border-color 0.2s'
  };

  const fmtK = (n) => n >= 1000 ? `${(n/1000).toFixed(0)}k` : n;

  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ maxWidth: '500px', background: 'white', borderRadius: '24px', padding: '3rem', textAlign: 'center', boxShadow: '0 10px 40px rgba(16,55,102,0.1)' }}>
          <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg, #103766, #1a5276)', margin: '0 auto 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '2.5rem' }}>&#10003;</div>
          <h2 style={{ fontSize: '1.75rem', fontWeight: '700', color: '#103766', marginBottom: '0.75rem' }}>{t('waitlist.successTitle')}</h2>
          <p style={{ color: '#64748b', marginBottom: '2rem' }}>{t('waitlist.successMessage')}</p>
          <button onClick={() => navigate('/login')} style={{ padding: '0.875rem 2rem', background: '#103766', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}>{t('waitlist.backToHome')}</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'white' }}>

      {/* ===== HERO ===== */}
      <div style={{ background: 'linear-gradient(160deg, #0c2d52 0%, #103766 50%, #1a5276 100%)', padding: '2rem 1.5rem 3.5rem', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-100px', left: '-100px', width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(255,153,0,0.08) 0%, transparent 70%)', borderRadius: '50%' }}></div>
        <div style={{ position: 'absolute', bottom: '-50px', right: '-50px', width: '200px', height: '200px', background: 'radial-gradient(circle, rgba(59,130,246,0.1) 0%, transparent 70%)', borderRadius: '50%' }}></div>
        
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1.5rem', position: 'relative', zIndex: 2 }}>
          <div style={{ display: 'flex', gap: '6px', background: 'rgba(255,255,255,0.1)', padding: '5px', borderRadius: '10px', backdropFilter: 'blur(8px)' }}>
            <button onClick={() => i18n.changeLanguage('es')} data-testid="lang-es" style={{ padding: '6px 14px', background: i18n.language === 'es' ? '#ff9900' : 'transparent', color: 'white', border: 'none', borderRadius: '7px', fontWeight: '600', cursor: 'pointer', fontSize: '12px' }}>Espanol</button>
            <button onClick={() => i18n.changeLanguage('ca')} data-testid="lang-ca" style={{ padding: '6px 14px', background: i18n.language === 'ca' ? '#ff9900' : 'transparent', color: 'white', border: 'none', borderRadius: '7px', fontWeight: '600', cursor: 'pointer', fontSize: '12px' }}>Catala</button>
          </div>
        </div>

        <div style={{ position: 'relative', zIndex: 1 }}>
          <h1 style={{ fontSize: 'clamp(2.75rem, 9vw, 4rem)', fontWeight: '800', color: 'white', marginBottom: '0.25rem', letterSpacing: '-0.02em' }}>Vivendius</h1>
          <p style={{ color: '#ff9900', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.3em', marginBottom: '1.5rem', fontWeight: '600' }}>{t('waitlist.tagline')}</p>
          
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.5rem', background: 'linear-gradient(135deg, rgba(255,153,0,0.2), rgba(255,153,0,0.1))', border: '1.5px solid rgba(255,153,0,0.5)', borderRadius: '50px', marginBottom: '2rem' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ff9900', boxShadow: '0 0 8px rgba(255,153,0,0.6)' }}></span>
            <span style={{ color: '#ff9900', fontWeight: '700', fontSize: '0.75rem', textTransform: 'uppercase' }}>{t('waitlist.onlyInvitation')}</span>
          </div>
          
          <h2 style={{ fontSize: 'clamp(1.25rem, 4vw, 1.75rem)', fontWeight: '700', color: 'white', marginBottom: '0.5rem', maxWidth: '600px', margin: '0 auto 0.5rem' }}>
            {isCa ? 'La millor rendibilitat passiva del mercat' : 'La mejor rentabilidad pasiva del mercado'}
          </h2>
          <p style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1.05rem)', color: 'rgba(255,255,255,0.6)', maxWidth: '500px', margin: '0 auto 2.5rem' }}>
            {isCa ? 'Rendiments que cap banc, ni pis comprat per llogar, poden oferir' : 'Rendimientos que ningun banco, ni piso comprado para alquilar, pueden ofrecer'}
          </p>
          
          <div style={{ maxWidth: '580px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
            {[
              { value: '6-9%', label: isCa ? 'GARANTIT' : 'GARANTIZADO' },
              { value: '100%', label: 'BARCELONA' },
              { value: '0%', label: isCa ? 'RISCOS' : 'RIESGOS' }
            ].map(({ value, label }) => (
              <div key={label} style={{ textAlign: 'center', padding: '1.25rem 0.5rem', background: 'rgba(255,255,255,0.07)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)' }}>
                <div style={{ fontSize: 'clamp(1.5rem, 5vw, 2rem)', fontWeight: '800', color: '#ff9900', lineHeight: 1 }}>{value}</div>
                <div style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: '0.4rem' }}>{label}</div>
              </div>
            ))}
          </div>

          {/* Referral bonus highlight */}
          <div style={{ maxWidth: '500px', margin: '1.5rem auto 0', padding: '0.9rem 1.25rem', background: 'rgba(255,153,0,0.1)', border: '1px solid rgba(255,153,0,0.3)', borderRadius: '12px' }}>
            <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)', margin: 0, lineHeight: 1.6, textAlign: 'center' }}>
              <span style={{ color: '#ff9900', fontWeight: '700' }}>+1%</span> {isCa
                ? 'extra si refereixes familiars o persones de confiança. Guanyes l\'1% sobre el capital de cada referit actiu.'
                : 'extra si refieres familiares o personas de confianza. Ganas el 1% sobre el capital de cada referido activo.'}
            </p>
          </div>
        </div>
      </div>

      {/* ===== COMPARISON: Why Vivendius ===== */}
      <div style={{ padding: '3.5rem 1.5rem', maxWidth: '950px', margin: '0 auto' }}>
        <h3 style={{ textAlign: 'center', fontSize: 'clamp(1.25rem, 4vw, 1.75rem)', fontWeight: '800', color: '#103766', marginBottom: '2.5rem' }}>
          {isCa ? 'Compara i decideix' : 'Compara y decide'}
        </h3>

        {/* Scenario 1: 50.000€ */}
        <p style={{ textAlign: 'center', color: '#103766', fontWeight: '700', fontSize: '1.1rem', marginBottom: '1.25rem', background: '#f0f7ff', padding: '0.6rem 1rem', borderRadius: '10px', display: 'inline-block', width: '100%', boxSizing: 'border-box' }}>
          {isCa ? 'Amb un préstec de 50.000€' : 'Con un préstamo de 50.000€'}
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '2.5rem' }}>
          <div style={{ padding: '1.5rem 1.25rem', borderRadius: '16px', border: '1.5px solid #e2e8f0', background: '#f8fafc', textAlign: 'center' }}>
            <h4 style={{ fontSize: '0.9rem', fontWeight: '700', color: '#94a3b8', marginBottom: '0.75rem' }}>
              {isCa ? 'Producte Bancari' : 'Producto Bancario'}
            </h4>
            <div style={{ fontSize: '2rem', fontWeight: '800', color: '#94a3b8' }}>2%</div>
            <div style={{ margin: '0.75rem 0', padding: '0.6rem', background: '#e2e8f0', borderRadius: '8px' }}>
              <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#64748b' }}>+83€/{isCa ? 'mes' : 'mes'}</div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>1.000€/{isCa ? 'any' : 'año'}</div>
            </div>
          </div>

          <div style={{ padding: '1.5rem 1.25rem', borderRadius: '16px', border: '1.5px solid #e2e8f0', background: '#f8fafc', textAlign: 'center' }}>
            <h4 style={{ fontSize: '0.9rem', fontWeight: '700', color: '#94a3b8', marginBottom: '0.75rem' }}>
              {isCa ? 'Comprar per Llogar' : 'Comprar para Alquilar'}
            </h4>
            <div style={{ fontSize: '2rem', fontWeight: '800', color: '#94a3b8' }}>3-4%</div>
            <div style={{ margin: '0.75rem 0', padding: '0.6rem', background: '#e2e8f0', borderRadius: '8px' }}>
              <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#64748b' }}>+146€/{isCa ? 'mes' : 'mes'}</div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{isCa ? 'brut - IBI, comunitat, derrames...' : 'bruto - IBI, comunidad, derramas...'}</div>
            </div>
          </div>

          <div style={{ padding: '1.5rem 1.25rem', borderRadius: '16px', border: '2.5px solid #ff9900', background: 'linear-gradient(160deg, #0c2d52 0%, #103766 100%)', textAlign: 'center', position: 'relative', boxShadow: '0 6px 24px rgba(255,153,0,0.15)' }}>
            <div style={{ position: 'absolute', top: '-11px', left: '50%', transform: 'translateX(-50%)', background: '#ff9900', color: 'white', padding: '0.25rem 0.9rem', borderRadius: '20px', fontSize: '0.65rem', fontWeight: '700', textTransform: 'uppercase' }}>
              {isCa ? 'Millor Opció' : 'Mejor Opción'}
            </div>
            <h4 style={{ fontSize: '0.9rem', fontWeight: '700', color: '#ff9900', marginBottom: '0.75rem' }}>Vivendius</h4>
            <div style={{ fontSize: '2rem', fontWeight: '800', color: 'white' }}>6%</div>
            <div style={{ margin: '0.75rem 0', padding: '0.6rem', background: 'rgba(255,153,0,0.2)', borderRadius: '8px', border: '1px solid rgba(255,153,0,0.3)' }}>
              <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#ff9900' }}>+250€/{isCa ? 'mes' : 'mes'}</div>
              <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)' }}>3.000€/{isCa ? 'any passivament' : 'año pasivamente'}</div>
            </div>
          </div>
        </div>

        {/* Scenario 2: 150.000€ */}
        <p style={{ textAlign: 'center', color: '#103766', fontWeight: '700', fontSize: '1.1rem', marginBottom: '1.25rem', background: '#f0f7ff', padding: '0.6rem 1rem', borderRadius: '10px', display: 'inline-block', width: '100%', boxSizing: 'border-box' }}>
          {isCa ? 'Amb un préstec de 150.000€' : 'Con un préstamo de 150.000€'}
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
          <div style={{ padding: '1.5rem 1.25rem', borderRadius: '16px', border: '1.5px solid #e2e8f0', background: '#f8fafc', textAlign: 'center' }}>
            <h4 style={{ fontSize: '0.9rem', fontWeight: '700', color: '#94a3b8', marginBottom: '0.75rem' }}>
              {isCa ? 'Producte Bancari' : 'Producto Bancario'}
            </h4>
            <div style={{ fontSize: '2rem', fontWeight: '800', color: '#94a3b8' }}>2%</div>
            <div style={{ margin: '0.75rem 0', padding: '0.6rem', background: '#e2e8f0', borderRadius: '8px' }}>
              <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#64748b' }}>+250€/{isCa ? 'mes' : 'mes'}</div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>3.000€/{isCa ? 'any' : 'año'}</div>
            </div>
          </div>

          <div style={{ padding: '1.5rem 1.25rem', borderRadius: '16px', border: '1.5px solid #e2e8f0', background: '#f8fafc', textAlign: 'center' }}>
            <h4 style={{ fontSize: '0.9rem', fontWeight: '700', color: '#94a3b8', marginBottom: '0.75rem' }}>
              {isCa ? 'Comprar per Llogar' : 'Comprar para Alquilar'}
            </h4>
            <div style={{ fontSize: '2rem', fontWeight: '800', color: '#94a3b8' }}>3-4%</div>
            <div style={{ margin: '0.75rem 0', padding: '0.6rem', background: '#e2e8f0', borderRadius: '8px' }}>
              <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#64748b' }}>+437€/{isCa ? 'mes' : 'mes'}</div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{isCa ? 'brut - IBI, comunitat, derrames...' : 'bruto - IBI, comunidad, derramas...'}</div>
            </div>
          </div>

          <div style={{ padding: '1.5rem 1.25rem', borderRadius: '16px', border: '2.5px solid #ff9900', background: 'linear-gradient(160deg, #0c2d52 0%, #103766 100%)', textAlign: 'center', position: 'relative', boxShadow: '0 6px 24px rgba(255,153,0,0.15)' }}>
            <div style={{ position: 'absolute', top: '-11px', left: '50%', transform: 'translateX(-50%)', background: '#ff9900', color: 'white', padding: '0.25rem 0.9rem', borderRadius: '20px', fontSize: '0.65rem', fontWeight: '700', textTransform: 'uppercase' }}>
              x4 {isCa ? 'més que el banc' : 'más que el banco'}
            </div>
            <h4 style={{ fontSize: '0.9rem', fontWeight: '700', color: '#ff9900', marginBottom: '0.75rem' }}>Vivendius</h4>
            <div style={{ fontSize: '2rem', fontWeight: '800', color: 'white' }}>8%</div>
            <div style={{ margin: '0.75rem 0', padding: '0.6rem', background: 'rgba(255,153,0,0.2)', borderRadius: '8px', border: '1px solid rgba(255,153,0,0.3)' }}>
              <div style={{ fontSize: '1.3rem', fontWeight: '800', color: '#ff9900' }}>+1.000€/{isCa ? 'mes' : 'mes'}</div>
              <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)' }}>12.000€/{isCa ? 'any passivament' : 'año pasivamente'}</div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== RATE TIERS ===== */}
      {rateTiers.length > 0 && (
      <div style={{ padding: '0 1.5rem 3.5rem', maxWidth: '700px', margin: '0 auto' }}>
        <h3 style={{ textAlign: 'center', fontSize: 'clamp(1.1rem, 3.5vw, 1.5rem)', fontWeight: '800', color: '#103766', marginBottom: '0.5rem' }}>
          {isCa ? 'Els nostres barems d\'interes' : 'Nuestros baremos de interes'}
        </h3>
        <p style={{ textAlign: 'center', color: '#64748b', marginBottom: '2rem', fontSize: '0.9rem' }}>
          {isCa ? 'Com mes capital, mes rendiment. Fix i garantit per contracte.' : 'Cuanto mas capital, mas rendimiento. Fijo y garantizado por contrato.'}
        </p>

        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {rateTiers.map((tier, i) => {
            const isTop = i === rateTiers.length - 1;
            return (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '1.25rem 1.5rem', borderRadius: '14px',
                background: isTop ? 'linear-gradient(135deg, #103766 0%, #1a5276 100%)' : '#f8fafc',
                border: isTop ? '2px solid #ff9900' : '1.5px solid #e2e8f0',
                boxShadow: isTop ? '0 4px 20px rgba(255,153,0,0.15)' : 'none'
              }}>
                <div>
                  <div style={{ fontSize: '0.95rem', fontWeight: '700', color: isTop ? 'white' : '#1e293b' }}>
                    {tier.min_capital >= 1000 ? `${(tier.min_capital/1000).toFixed(0)}.000` : tier.min_capital}€
                    {tier.max_capital ? ` - ${(tier.max_capital/1000).toFixed(0)}.000€` : '+'}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: isTop ? 'rgba(255,255,255,0.6)' : '#94a3b8', marginTop: '0.15rem' }}>
                    {isCa ? 'Capital invertit' : 'Capital invertido'}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '1.75rem', fontWeight: '800', color: isTop ? '#ff9900' : '#103766' }}>
                    {tier.interest_rate}%
                  </div>
                  <div style={{ fontSize: '0.7rem', color: isTop ? 'rgba(255,255,255,0.5)' : '#94a3b8', textTransform: 'uppercase' }}>
                    {isCa ? 'anual fix' : 'anual fijo'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* ===== TRUST & CONFIDENCE ===== */}
      <div style={{ padding: '0 1.5rem 3rem', maxWidth: '700px', margin: '0 auto' }}>
        <div style={{ padding: '2rem', background: 'linear-gradient(135deg, #103766 0%, #1a5276 100%)', borderRadius: '18px', boxShadow: '0 4px 20px rgba(16,55,102,0.2)' }}>
          <h3 style={{ color: '#ff9900', fontSize: '1.15rem', fontWeight: '700', marginBottom: '1rem', textAlign: 'center' }}>
            {isCa ? 'Un club tancat per la teva seguretat' : 'Un club cerrado por tu seguridad'}
          </h3>
          <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.95rem', lineHeight: 1.9, margin: '0 0 1rem 0' }}>
            {isCa
              ? 'Vivendius no és una plataforma d\'inversió amb percentatges estimats ni projeccions a dos o tres anys que després no es compleixen. És un club privat tancat, precisament perquè els prestecs entre particular i empresa siguin 100% segurs, garantits i només dins d\'un entorn de confiança.'
              : 'Vivendius no es una plataforma de inversion con porcentajes estimados ni proyecciones a dos o tres anos que despues no se cumplen. Es un club privado cerrado, precisamente para que los prestamos entre particular y empresa sean 100% seguros, garantizados y solo dentro de un entorno de confianza.'}
          </p>
          <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.95rem', lineHeight: 1.9, margin: 0 }}>
            {isCa
              ? 'Des de la direcció fins a l\'entorn familiar, amics i persones que ja han confiat en Vivendius per comprar les seves vivendes. Per això funciona només per invitació.'
              : 'Desde la direccion hasta el entorno familiar, amigos y personas que ya han confiado en Vivendius para comprar sus viviendas. Por eso funciona solo por invitacion.'}
          </p>
        </div>

        {/* Link to projects */}
        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <a href="https://www.vivendius.cat" target="_blank" rel="noopener noreferrer" style={{
            display: 'inline-block', padding: '0.9rem 2rem',
            background: 'white', color: '#103766', fontWeight: '700', fontSize: '0.95rem',
            borderRadius: '12px', textDecoration: 'none',
            boxShadow: '0 4px 16px rgba(16,55,102,0.1)', border: '1.5px solid #e2e8f0',
            transition: 'box-shadow 0.2s'
          }}>
            {isCa
              ? 'Veure els projectes actuals en marxa \u2192'
              : 'Ver los proyectos actuales en marcha \u2192'}
          </a>
          <p style={{ color: '#94a3b8', fontSize: '0.8rem', marginTop: '0.5rem' }}>
            {isCa
              ? 'Projectes tangibles a Barcelona, sempre a menys de 6 mesos'
              : 'Proyectos tangibles en Barcelona, siempre a menos de 6 meses'}
          </p>
        </div>
      </div>
      {([1, 2].some(n => {
        const url = landingSettings[`video${n}_url`] || '';
        return url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
      })) && (
      <div style={{ padding: '0 1.5rem 3rem', maxWidth: '800px', margin: '0 auto' }}>
        <h3 style={{ textAlign: 'center', fontSize: '1.4rem', fontWeight: '700', color: '#103766', marginBottom: '0.5rem' }}>
          {isCa ? 'Descobreix Vivendius' : 'Descubre Vivendius'}
        </h3>
        <p style={{ textAlign: 'center', color: '#64748b', marginBottom: '2rem', fontSize: '0.95rem' }}>
          {isCa ? 'Coneix les avantatges de formar part del nostre club d\'inversors' : 'Conoce las ventajas de formar parte de nuestro club de inversores'}
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
          {[1, 2].map(n => {
            const url = landingSettings[`video${n}_url`] || '';
            const title = landingSettings[`video${n}_title_${isCa ? 'ca' : 'es'}`] || '';
            const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
            if (!ytMatch) return null;
            return (
              <div key={n} style={{ borderRadius: '14px', overflow: 'hidden', background: '#0c2d52', boxShadow: '0 4px 16px rgba(16,55,102,0.15)' }}>
                <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                  <iframe style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} src={`https://www.youtube.com/embed/${ytMatch[1]}`} title={title} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                </div>
                {title && <div style={{ padding: '1rem' }}><p style={{ fontWeight: '600', color: 'white', fontSize: '0.9rem', margin: 0 }}>{title}</p></div>}
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* ===== FORM ===== */}
      <div style={{ padding: '0 1.5rem 4rem', maxWidth: '520px', margin: '0 auto' }}>
        <div style={{ background: 'white', borderRadius: '20px', padding: '2.5rem 2rem', boxShadow: '0 4px 24px rgba(16,55,102,0.08)', border: '1px solid #e2e8f0' }}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem', paddingBottom: '1.25rem', borderBottom: '1px solid #e2e8f0' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#103766', marginBottom: '0.25rem' }}>
              {isCa ? 'Sol\u00b7licita Acces' : 'Solicita Acceso'}
            </h3>
            <p style={{ color: '#94a3b8', fontSize: '0.8rem' }}>{t('waitlist.privateExclusiveClub')}</p>
          </div>

          <form onSubmit={handleSubmit}>
            {[
              { label: t('waitlist.nombre'), field: 'nombre', type: 'text', placeholder: 'Joan Garcia' },
              { label: t('waitlist.email'), field: 'email', type: 'email', placeholder: 'joan@example.com' },
              { label: t('waitlist.telefono'), field: 'telefono', type: 'tel', placeholder: '+34 600 000 000' },
            ].map(({ label, field, type, placeholder }) => (
              <div key={field} style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontWeight: '600', color: '#334155', marginBottom: '0.4rem', fontSize: '0.8rem' }}>{label} *</label>
                <input type={type} required value={formData[field]} onChange={(e) => setFormData({ ...formData, [field]: e.target.value })} style={inputStyle} placeholder={placeholder} />
              </div>
            ))}

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontWeight: '600', color: '#334155', marginBottom: '0.4rem', fontSize: '0.8rem' }}>{t('waitlist.capitalDisponible')} (€) *</label>
              <input type="number" required min={minAmount} step="1000" value={formData.capital_disponible} onChange={(e) => setFormData({ ...formData, capital_disponible: e.target.value })} style={inputStyle} placeholder={minAmount.toString()} />
              <p style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '0.3rem' }}>{t('waitlist.minAmount', { amount: minAmount.toLocaleString('es-ES') })}</p>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontWeight: '600', color: '#334155', marginBottom: '0.4rem', fontSize: '0.8rem' }}>{t('waitlist.tipoInversion')} *</label>
              <select required value={formData.tipo_inversion} onChange={(e) => setFormData({ ...formData, tipo_inversion: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="short_term">{t('waitlist.shortTerm')}</option>
                <option value="medium_term">{t('waitlist.mediumTerm')}</option>
                <option value="long_term">{t('waitlist.longTerm')}</option>
              </select>
            </div>

            <div style={{ marginBottom: '1.5rem', padding: '1.25rem', background: '#f0f7ff', border: '2px solid #103766', borderRadius: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '700', color: '#103766', marginBottom: '0.5rem', fontSize: '0.8rem', textTransform: 'uppercase' }}>
                {t('waitlist.invitationCode')} *
                <span style={{ fontSize: '0.65rem', background: '#103766', color: 'white', padding: '0.2rem 0.6rem', borderRadius: '10px' }}>{isCa ? 'OBLIGATORI' : 'OBLIGATORIO'}</span>
              </label>
              <div style={{ position: 'relative' }}>
                <input type="text" required value={formData.invitation_code}
                  onChange={(e) => { setFormData({ ...formData, invitation_code: e.target.value.toUpperCase() }); validateInvitationCode(e.target.value.toUpperCase()); }}
                  style={{
                    ...inputStyle, paddingRight: '3rem', fontFamily: 'monospace', fontSize: '1.1rem', fontWeight: '700', letterSpacing: '0.1em', textAlign: 'center',
                    background: codeValid === true ? '#f0fdf4' : codeValid === false ? '#fef2f2' : '#f8fafc',
                    borderColor: codeValid === true ? '#22c55e' : codeValid === false ? '#ef4444' : '#e2e8f0'
                  }}
                  placeholder="VIV-XXXXXXXX"
                />
                {codeValid === true && <span style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#22c55e', fontSize: '1.25rem' }}>&#10003;</span>}
                {codeValid === false && <span style={{ position: 'absolute', right: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#ef4444', fontSize: '1.25rem' }}>&#10005;</span>}
              </div>
            </div>

            <button type="submit" disabled={submitting || !codeValid} data-testid="submit-waitlist" style={{
              width: '100%', padding: '1rem', border: 'none', borderRadius: '10px', fontWeight: '700', fontSize: '1rem', cursor: (submitting || !codeValid) ? 'not-allowed' : 'pointer',
              background: (submitting || !codeValid) ? '#e2e8f0' : 'linear-gradient(135deg, #103766 0%, #1a5276 100%)', color: (submitting || !codeValid) ? '#94a3b8' : 'white',
              boxShadow: (submitting || !codeValid) ? 'none' : '0 4px 16px rgba(16,55,102,0.35)'
            }}>
              {submitting ? '...' : !codeValid ? t('waitlist.codeRequired') : t('waitlist.joinWaitlist')}
            </button>

            <div style={{ marginTop: '1.25rem', textAlign: 'center' }}>
              <a href="/login" onClick={(e) => { e.preventDefault(); navigate('/login'); }} style={{ color: '#64748b', textDecoration: 'none', fontSize: '0.85rem' }}>
                {t('waitlist.alreadyHaveAccount')} &rarr;
              </a>
            </div>
          </form>
        </div>
      </div>

      {/* Footer */}
      <div style={{ padding: '1.5rem', textAlign: 'center', background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
        <p style={{ color: '#94a3b8', fontSize: '0.8rem', margin: '0 0 0.5rem 0' }}>Investments Made Easy, S.L. - CIF: B67108373</p>
        <p style={{ color: '#b0b8c4', fontSize: '0.7rem', margin: 0 }}>Projecte by <a href="https://www.digitalsunriselabs.com/" target="_blank" rel="noopener noreferrer" style={{ color: '#b0b8c4', textDecoration: 'none' }}>Digital Sunrise Labs&reg;</a></p>
      </div>
    </div>
  );
}

export default LandingPage;
